
import Vue from 'vue';
import { mapActions } from 'vuex';
import moment from 'moment';
import { functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { DbTimestamp } from '@run-diffusion/shared';
import BaseButton from '@/components/base/BaseButton.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import TokenRevealDialog from '@/views/Team/components/TokenRevealDialog.vue';

/** Stage 01: everything the Identity Provider needs to reach our SCIM server. */
export default Vue.extend({
	name: 'ScimConnectStep',
	components: {
		BaseButton,
		ClipboardCopy,
		ConfirmDialog,
		TokenRevealDialog,
	},
	props: {
		companyId: { type: String, default: '' },
		scimTokenLast4: { type: String, default: null },
		groupsReceived: { type: Number, default: 0 },
		lastPushAt: { type: Object as () => DbTimestamp | null, default: null },
		memberEmailsReceived: { type: Number, default: 0 },
	},
	data () {
		return {
			loadingScimToken: false,
			tokenModalOpen: false,
			tokenJustGenerated: null as string | null,
		};
	},
	computed: {
		/** Customer-facing SCIM base (Firebase Hosting → Cloud Function `scim`). */
		scimBaseUrl (): string {
			return 'https://api1.rundiffusion.com/scim/v2';
		},
		lastPushLabel (): string {
			if (!this.lastPushAt) return 'No group push received yet.';
			const when = moment(this.lastPushAt.toMillis()).format('MMM D, h:mm a');
			const groups = `${this.groupsReceived} ${this.groupsReceived === 1 ? 'group' : 'groups'}`;
			const users = `${this.memberEmailsReceived} ${this.memberEmailsReceived === 1 ? 'user' : 'users'}`;
			return `Last push from your Identity Provider: ${when} · ${groups}, ${users}`;
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		openScimConfigurationLink () {
			window.open('https://view.monday.com/18404123239-d635e5eb88f0865b232237b2a639a5a3?r=use1', '_blank');
		},
		async onGenerateToken () {
			await this.createOrRollScimToken();
		},
		async onConfirmRollToken (close?: () => void) {
			await this.createOrRollScimToken();
			if (close) close();
		},
		async createOrRollScimToken () {
			if (!this.companyId) return;
			this.loadingScimToken = true;
			try {
				const fn = functions.httpsCallable('createOrRollAccessToken');
				const result = await fn({ companyId: this.companyId, useCase: 'scim' });
				const data = result?.data as { success?: boolean; token?: string; message?: string };
				if (data?.success && data?.token) {
					this.tokenJustGenerated = data.token;
					this.tokenModalOpen = true;
					this.$emit('scim-token-created');
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Failed to generate SCIM token',
						show: true,
					});
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: (e as { message?: string })?.message || 'Failed to generate SCIM token',
					show: true,
				});
			} finally {
				this.loadingScimToken = false;
			}
		},
	},
});
